// ═══════════════════════════════════════════════════════
// CLIENT SUITABILITY GATE
// Wizard auto-serviço para o cliente preencher o perfil
// de investidor (API — CVM Resolução 30) no primeiro acesso.
// ═══════════════════════════════════════════════════════
import { useState, type ReactElement } from "react";
import { type ThemeObject, DARK } from "../shared/theme.js";
import { upsertSuitability, logAudit } from "../supabaseClient.js";
import { logError } from "../shared/logger.js";
import { flexRow } from "../shared/styles.js";

interface SuitabilityPayload {
  nomeCompleto: string;
  dataNascimento: string;
  profissao: string;
  estadoCivil: string;
  objetivos: string[];
  expNivel: number;
  reacaoQueda: string;
  horizonte: string;
  investQualif: boolean;
  scoreSuit?: number;
  scoreMax?: number;
  perfilResult?: string;
  status?: string;
  assinaturaCliente?: boolean;
  assinaturaConsultor?: boolean;
  lastUpdate?: string;
}

interface ClientSuitabilityGateProps {
  clientId: string;
  profile?: { full_name?: string } | null;
  onComplete: (payload: SuitabilityPayload | null) => void;
  T: ThemeObject;
}

export default function ClientSuitabilityGate({
  clientId,
  profile,
  onComplete,
  T,
}: ClientSuitabilityGateProps) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<SuitabilityPayload>({
    nomeCompleto: profile?.full_name || "",
    dataNascimento: "",
    profissao: "",
    estadoCivil: "Solteiro",
    objetivos: [],
    expNivel: 2,
    reacaoQueda: "moderado",
    horizonte: "longo",
    investQualif: false,
  });

  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }) as SuitabilityPayload);
  const togObj = (k: string) =>
    setForm((f) => ({
      ...f,
      objetivos: f.objetivos.includes(k) ? f.objetivos.filter((x) => x !== k) : [...f.objetivos, k],
    }));

  const calcScore = (f: SuitabilityPayload) => {
    const e = f.expNivel === 1 ? 3 : f.expNivel === 2 ? 7 : 10;
    const r = f.reacaoQueda === "conservador" ? 2 : f.reacaoQueda === "moderado" ? 5 : 8;
    const h = f.horizonte === "curto" ? 2 : f.horizonte === "medio" ? 4 : 7;
    const q = f.investQualif ? 5 : 0;
    const total = Math.min(e + r + h + q, 25);
    const perfil =
      total <= 7
        ? "Conservador"
        : total <= 13
          ? "Moderado"
          : total <= 19
            ? "Arrojado"
            : "Agressivo";
    return { score: total, max: 25, perfil };
  };

  const perfilColor: Record<string, string> = {
    Conservador: "#16A34A",
    Moderado: "#D97706",
    Arrojado: "#7C3AED",
    Agressivo: "#DC2626",
  };
  const perfilDesc: Record<string, string> = {
    Conservador:
      "Prioriza segurança e previsibilidade. Prefere renda fixa conservadora e baixa volatilidade.",
    Moderado:
      "Aceita alguma oscilação em busca de rentabilidade acima do CDI. Portfólio equilibrado.",
    Arrojado: "Tolera volatilidade moderada-alta em busca de retornos acima da média do mercado.",
    Agressivo:
      "Alta tolerância ao risco. Foco em retorno de longo prazo com renda variável e alternativos.",
  };

  const handleFinish = async () => {
    setSaving(true);
    const s = calcScore(form);
    const payload: SuitabilityPayload = {
      ...form,
      scoreSuit: s.score,
      scoreMax: s.max,
      perfilResult: s.perfil,
      status: "parcial",
      assinaturaCliente: true,
      assinaturaConsultor: false,
      lastUpdate: new Date().toISOString().slice(0, 10),
    };
    try {
      await upsertSuitability(
        clientId,
        payload as unknown as Parameters<typeof upsertSuitability>[1]
      );
      await logAudit("client_suitability_self", {
        clientId: clientId,
        details: {
          perfil: s.perfil,
          score: s.score,
        },
      });
      onComplete(payload);
    } catch (e) {
      logError("ClientSuitabilityGate", e, { context: "save suitability" });
      onComplete(payload);
    } finally {
      setSaving(false);
    }
  };

  const inp = (lbl: string, key: string, type = "text", ph = "") => (
    <div style={{ marginBottom: 16 }}>
      <label
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 700,
          color: T.sub,
          marginBottom: 5,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {lbl}
      </label>
      <input
        type={type}
        value={(form as unknown as Record<string, string>)[key] || ""}
        onChange={(e) => set(key, e.target.value)}
        placeholder={ph}
        style={{
          width: "100%",
          padding: "10px 13px",
          borderRadius: 9,
          border: `1.5px solid ${T.border}`,
          background: T.bg,
          color: T.text,
          fontSize: 14,
          outline: "none",
          boxSizing: "border-box",
        }}
      />
    </div>
  );

  const selEl = (lbl: string, key: string, opts: [string | boolean, string][]) => (
    <div style={{ marginBottom: 16 }}>
      <label
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 700,
          color: T.sub,
          marginBottom: 5,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {lbl}
      </label>
      <select
        value={String((form as unknown as Record<string, unknown>)[key] || "")}
        onChange={(e) =>
          set(
            key,
            e.target.value === "true" ? true : e.target.value === "false" ? false : e.target.value
          )
        }
        style={{
          width: "100%",
          padding: "10px 13px",
          borderRadius: 9,
          border: `1.5px solid ${T.border}`,
          background: T.bg,
          color: T.text,
          fontSize: 14,
        }}
      >
        {opts.map(([v, l]) => (
          <option key={String(v)} value={String(v)}>
            {l}
          </option>
        ))}
      </select>
    </div>
  );

  const radioCard = (
    key: string,
    val: string | number,
    emoji: string,
    lbl: string,
    desc?: string
  ) => (
    <label
      key={String(val)}
      onClick={() => set(key, val)}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 10,
        marginBottom: 8,
        cursor: "pointer",
        background:
          (form as unknown as Record<string, unknown>)[key] === val ? `${T.accent}12` : T.bg,
        border: `1.5px solid ${(form as unknown as Record<string, unknown>)[key] === val ? T.accent : T.border}`,
        transition: "all 0.15s",
      }}
    >
      <div style={{ fontSize: 20, minWidth: 24, marginTop: 1 }}>{emoji}</div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{lbl}</div>
        {desc && <div style={{ fontSize: 12, color: T.sub, marginTop: 2 }}>{desc}</div>}
      </div>
      {(form as unknown as Record<string, unknown>)[key] === val && (
        <div style={{ marginLeft: "auto", color: T.accent, fontSize: 18, fontWeight: 700 }}>✓</div>
      )}
    </label>
  );

  const objCard = (k: string, emoji: string, lbl: string) => {
    const sel = form.objetivos.includes(k);
    return (
      <label
        key={k}
        onClick={() => togObj(k)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "11px 14px",
          borderRadius: 10,
          marginBottom: 8,
          cursor: "pointer",
          background: sel ? `${T.accent}12` : T.bg,
          border: `1.5px solid ${sel ? T.accent : T.border}`,
          transition: "all 0.15s",
        }}
      >
        <div style={{ fontSize: 18 }}>{emoji}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, flex: 1 }}>{lbl}</div>
        <div
          style={{
            width: 18,
            height: 18,
            borderRadius: 5,
            border: `2px solid ${sel ? T.accent : T.border}`,
            background: sel ? T.accent : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {sel && <span style={{ color: "#fff", fontSize: 11, fontWeight: 900 }}>✓</span>}
        </div>
      </label>
    );
  };

  const TOTAL_STEPS = 5;
  const pct = Math.round((step / (TOTAL_STEPS - 1)) * 100);

  const CONTENT: ReactElement[] = [
    /* 0 — Boas-vindas */
    <>
      <div style={{ textAlign: "center", padding: "16px 0 24px" }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 18,
            background: "#C9A227",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 30,
            fontWeight: 900,
            color: "#0B2447",
            margin: "0 auto 20px",
          }}
        >
          L
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: "0 0 10px" }}>
          Bem-vindo ao LPA Wealth Portal
        </h2>
        <p style={{ fontSize: 14, color: T.sub, margin: "0 0 8px", lineHeight: 1.6 }}>
          Antes de acessar seu portal personalizado, precisamos realizar um breve questionário de
          adequação de perfil de investidor.
        </p>
        <p style={{ fontSize: 13, color: T.muted, margin: 0, lineHeight: 1.6 }}>
          Conforme a Resolução CVM 30, o formulário de suitability é obrigatório para garantir que
          as recomendações de investimento sejam compatíveis com seus objetivos e tolerância a
          risco.
        </p>
      </div>
      <div
        style={{
          background: T === DARK ? "rgba(37,99,235,0.1)" : "#F0F9FF",
          border: `1px solid ${T === DARK ? "#1E3A5F" : "#BAE6FD"}`,
          borderRadius: 10,
          padding: "12px 16px",
          marginBottom: 8,
        }}
      >
        <p style={{ margin: 0, fontSize: 12, color: T === DARK ? "#7DD3FC" : "#0369A1" }}>
          ⏱️ <strong>Tempo estimado:</strong> 3-5 minutos · Suas respostas ficam salvas
          automaticamente.
        </p>
      </div>
    </>,
    /* 1 — Dados básicos */
    <>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: "0 0 18px" }}>
        📋 Dados Pessoais
      </h3>
      {inp("Nome completo", "nomeCompleto", "text", "Seu nome completo")}
      {inp("Data de nascimento", "dataNascimento", "date")}
      {inp("Profissão / Ocupação", "profissao", "text", "Ex: Empresário, Médico, Engenheiro...")}
      {selEl("Estado civil", "estadoCivil", [
        ["Solteiro", "Solteiro(a)"],
        ["Casado", "Casado(a)"],
        ["Divorciado", "Divorciado(a)"],
        ["Viúvo", "Viúvo(a)"],
        ["União Estável", "União Estável"],
      ])}
    </>,
    /* 2 — Objetivos */
    <>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: "0 0 6px" }}>
        🎯 Objetivos Financeiros
      </h3>
      <p style={{ fontSize: 13, color: T.sub, margin: "0 0 16px" }}>
        Selecione todos que se aplicam:
      </p>
      {objCard("preservar", "🛡️", "Preservar patrimônio (proteção contra inflação)")}
      {objCard("crescer", "📈", "Fazer o patrimônio crescer acima do CDI")}
      {objCard("aposentar", "🏖️", "Planejar a aposentadoria")}
      {objCard("renda", "💰", "Gerar renda mensal / dividendos")}
      {objCard("heranca", "🏛️", "Planejamento sucessório / herança")}
      {objCard("exterior", "🌍", "Diversificação em moeda estrangeira")}
      {form.objetivos.length === 0 && (
        <div style={{ fontSize: 12, color: "#DC2626", marginTop: 4 }}>
          Selecione ao menos um objetivo para continuar.
        </div>
      )}
    </>,
    /* 3 — Perfil de risco */
    <>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: "0 0 18px" }}>
        🧭 Perfil de Investidor
      </h3>
      <div style={{ marginBottom: 18 }}>
        <p
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: T.sub,
            margin: "0 0 8px",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          Sua experiência com investimentos
        </p>
        {radioCard(
          "expNivel",
          1,
          "🌱",
          "Iniciante",
          "Tenho pouca ou nenhuma experiência com produtos financeiros além da poupança."
        )}
        {radioCard(
          "expNivel",
          2,
          "📊",
          "Intermediário",
          "Conheço renda fixa, fundos e já investi em alguns produtos no mercado."
        )}
        {radioCard(
          "expNivel",
          3,
          "🏦",
          "Experiente",
          "Tenho ampla experiência com renda fixa, variável, derivativos ou produtos no exterior."
        )}
      </div>
      <div style={{ marginBottom: 18 }}>
        <p
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: T.sub,
            margin: "0 0 8px",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          Se sua carteira caísse 10%, você:
        </p>
        {radioCard(
          "reacaoQueda",
          "conservador",
          "😰",
          "Venderia tudo",
          "Prefiro evitar perdas a qualquer custo, mesmo com menor rentabilidade."
        )}
        {radioCard(
          "reacaoQueda",
          "moderado",
          "🤔",
          "Aguardaria",
          "Manteria a posição e esperaria a recuperação do mercado."
        )}
        {radioCard(
          "reacaoQueda",
          "agressivo",
          "💪",
          "Compraria mais",
          "Aproveitaria a queda para aumentar minha posição."
        )}
      </div>
      <div style={{ marginBottom: 18 }}>
        <p
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: T.sub,
            margin: "0 0 8px",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          Horizonte de investimento
        </p>
        {radioCard(
          "horizonte",
          "curto",
          "📅",
          "Curto prazo (até 2 anos)",
          "Posso precisar resgatar em breve."
        )}
        {radioCard(
          "horizonte",
          "medio",
          "📆",
          "Médio prazo (2 a 5 anos)",
          "Não tenho urgência, mas podem surgir necessidades."
        )}
        {radioCard(
          "horizonte",
          "longo",
          "🗓️",
          "Longo prazo (acima de 5 anos)",
          "Invisto pensando no futuro e não pretendo resgatar cedo."
        )}
      </div>
      {selEl(
        "Você se enquadra como Investidor Qualificado? (patrimônio financeiro acima de R$ 1 MM)",
        "investQualif",
        [
          [false, "Não — patrimônio financeiro abaixo de R$ 1.000.000"],
          [true, "Sim — patrimônio financeiro acima de R$ 1.000.000"],
        ]
      )}
    </>,
    /* 4 — Resultado */
    (() => {
      const s = calcScore(form);
      const cor = perfilColor[s.perfil] || "#64748B";
      return (
        <>
          <div style={{ textAlign: "center", padding: "8px 0 24px" }}>
            <div style={{ fontSize: 48, marginBottom: 10 }}>
              {{ Conservador: "🛡️", Moderado: "⚖️", Arrojado: "🚀", Agressivo: "⚡" }[s.perfil]}
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 800, color: cor, margin: "0 0 8px" }}>
              Perfil {s.perfil}
            </h3>
            <p style={{ fontSize: 13, color: T.sub, margin: "0 0 16px", lineHeight: 1.6 }}>
              {perfilDesc[s.perfil]}
            </p>
            <div
              style={{
                display: "inline-flex",
                gap: 6,
                alignItems: "center",
                background: T.bg,
                border: `1.5px solid ${cor}`,
                borderRadius: 30,
                padding: "6px 16px",
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 700, color: cor }}>
                Score: {s.score}/{s.max}
              </span>
            </div>
          </div>
          <div
            style={{
              background: T.bg,
              border: `1px solid ${T.border}`,
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 16,
            }}
          >
            {(
              [
                [
                  "Experiência",
                  (
                    { 1: "Iniciante", 2: "Intermediário", 3: "Experiente" } as Record<
                      number,
                      string
                    >
                  )[form.expNivel],
                ],
                [
                  "Reação a quedas",
                  (
                    {
                      conservador: "Vende / sai",
                      moderado: "Aguarda",
                      agressivo: "Compra mais",
                    } as Record<string, string>
                  )[form.reacaoQueda],
                ],
                [
                  "Horizonte",
                  (
                    { curto: "Curto prazo", medio: "Médio prazo", longo: "Longo prazo" } as Record<
                      string,
                      string
                    >
                  )[form.horizonte],
                ],
                ["Inv. Qualificado", form.investQualif ? "Sim" : "Não"],
                ["Objetivos", form.objetivos.length > 0 ? form.objetivos.join(", ") : "–"],
              ] as [string, string | undefined][]
            ).map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px 0",
                  borderBottom: `1px solid ${T.border}`,
                }}
              >
                <span style={{ fontSize: 12, color: T.sub }}>{k}</span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: T.text,
                    textAlign: "right",
                    maxWidth: "60%",
                    wordBreak: "break-word",
                  }}
                >
                  {v}
                </span>
              </div>
            ))}
          </div>
          <div
            style={{
              background: "#DCFCE7",
              border: "1px solid #86EFAC",
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 8,
            }}
          >
            <p style={{ margin: 0, fontSize: 12, color: "#15803D" }}>
              ✅ Suas respostas serão revisadas pelo assessor LPA. O perfil será validado em até
              48h. Você pode atualizar suas informações a qualquer momento na seção Perfil.
            </p>
          </div>
        </>
      );
    })(),
  ];

  const canNext = [true, !!form.nomeCompleto?.trim(), form.objetivos.length > 0, true];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        fontFamily: "'Inter','Segoe UI',sans-serif",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "32px 16px 80px",
      }}
    >
      <div style={{ width: "100%", maxWidth: 560 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 28,
          }}
        >
          <div style={{ ...flexRow, gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: "#C9A227",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 900,
                color: "#0B2447",
                fontSize: 16,
              }}
            >
              L
            </div>
            <span style={{ fontSize: 14, fontWeight: 800, color: T.text }}>
              LPA <span style={{ color: "#C9A227" }}>Wealth</span>
            </span>
          </div>
          {step > 0 && (
            <span style={{ fontSize: 12, color: T.sub }}>
              {step}/{TOTAL_STEPS - 1}
            </span>
          )}
        </div>

        {step > 0 && (
          <div
            style={{
              height: 5,
              background: T.border,
              borderRadius: 10,
              marginBottom: 28,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${pct}%`,
                background: T.accent,
                borderRadius: 10,
                transition: "width 0.4s ease",
              }}
            />
          </div>
        )}

        <div
          style={{
            background: T.card,
            borderRadius: 16,
            padding: "28px 28px 24px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            marginBottom: 20,
          }}
        >
          {CONTENT[step]}
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {step > 0 ? (
            <button
              onClick={() => setStep((s) => s - 1)}
              style={{
                padding: "11px 22px",
                borderRadius: 10,
                border: `1.5px solid ${T.border}`,
                background: "transparent",
                color: T.sub,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              ← Voltar
            </button>
          ) : (
            <div />
          )}

          {step < TOTAL_STEPS - 1 ? (
            <button
              onClick={() => {
                if (canNext[step]) setStep((s) => s + 1);
              }}
              disabled={!canNext[step]}
              style={{
                padding: "11px 28px",
                borderRadius: 10,
                border: "none",
                background: canNext[step] ? T.accent : T.muted,
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
                cursor: canNext[step] ? "pointer" : "not-allowed",
                transition: "background 0.15s",
              }}
            >
              {step === 0 ? "Começar questionário →" : "Continuar →"}
            </button>
          ) : (
            <button
              onClick={handleFinish}
              disabled={saving}
              style={{
                padding: "11px 28px",
                borderRadius: 10,
                border: "none",
                background: saving ? "#94A3B8" : "#16A34A",
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
                cursor: saving ? "wait" : "pointer",
              }}
            >
              {saving ? "Salvando…" : "✅ Confirmar e Acessar Portal"}
            </button>
          )}
        </div>

        {step === 0 && (
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button
              onClick={() => onComplete(null)}
              style={{
                background: "none",
                border: "none",
                color: T.muted,
                fontSize: 12,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Preencher depois (acesso temporário)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
